'use client';

import { c as _c } from "react/compiler-runtime";
import { useUploadImage } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Crop, PixelCrop } from 'react-image-crop';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useGetImagesQuota, useMorfianateImage } from '@morfi/backend-sdk-v2/client';
import { GeneratingFrame } from '@/components/images/generating-frame';
import { shouldFetchImageQuota } from '@/components/images/image-quota-eligibility';
import { ImageShimmer, OptimizedImage } from '@/components/images/optimized-image';
import { IMAGE_UPLOAD_ACCEPT_ATTR, IMAGE_UPLOAD_MAX_MB, validateImageUpload } from '@/components/images/validation';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useAnnouncer } from '@/components/ui/announcer';
import { AppTabs, type AppTabsItem } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { Camera01Icon, Loading03Icon, PaintBoardIcon, SparklesIcon, Tick02Icon, Upload01Icon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { TOAST_IDS } from '@/config/toast-ids';
import { useNsfwStatusByUrl } from '@/hooks/use-nsfw-status-by-url';
import { checkGate } from '@/lib/analytics';
import { runWithCleanup } from '@/lib/async/run-with-cleanup';
import { getCroppedBlob } from '@/lib/images/get-cropped-blob';
import { hashStringToSeed, type PaletteName, pickBrandBluePalette } from '@/lib/pattern-studio';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useUploadTrackingStore } from '@/stores/upload-tracking';
export interface ImagePresetGroup {
  /** Image URLs (data: URLs or absolute) rendered as selectable thumbnails. */
  items: ReadonlyArray<string>;
  /** Tailwind grid template for the preset row, e.g. `'grid-cols-4'`. */
  gridClassName: string;
  /** Tailwind classes applied to each preset swatch (size, rounded, etc). */
  itemClassName: string;
}
export interface ImageCropConfig {
  /** Aspect ratio of the crop region. `1` for square, `16 / 9` for video, `3` for hero, etc. */
  aspect: number;
  /** Render the crop region as a circle? */
  circular?: boolean;
  /** Output canvas size (px) for the cropped PNG. The image is resized to this on confirm. */
  outputSize: {
    w: number;
    h: number;
  };
  /** File-name stem used when uploading the cropped blob. Default `image`. */
  fileNameStem?: string;
}
export interface CropModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional optimistic-display callback. Fired once with a `blob:` URL the
   * INSTANT the user clicks Confirm in the crop view — before Pinata runs.
   * Lets the parent advance its UI (wizard step, modal close) with zero
   * perceived latency. The IPFS upload runs fire-and-forget in the
   * background; `onUrlChange` is fired with the real IPFS URL when it
   * resolves. The blob URL is automatically revoked after resolve / reject.
   *
   * If omitted, the modal still closes immediately on Confirm and the IPFS
   * URL flows through `onUrlChange` once Pinata returns.
   */
  onPendingUpload?: (blobUrl: string) => void;
  /**
   * Owning user's id. Used to derive the brand-blue palette + seed for the
   * post-crop preview render through `<OptimizedImage>` so the user sees the
   * formatted artefact (what every visitor will see) before upload. When
   * undefined (e.g. launchpad / market cover uploads where the upload is
   * not user-identity-bound), the preview falls back to the lib default
   * palette + a stable seed.
   */
  userId?: string;
  /** Currently saved image URL — drives the "selected" highlight in the preset grid. */
  currentUrl: string | null;
  /**
   * Called with the chosen URL after a successful upload, preset confirmation, or removal.
   * Empty string indicates the user removed the image (only fired when `showRemove` is set).
   * The consumer is responsible for any further persistence (e.g. `updateProfile`,
   * Zustand-store writes, server actions). The modal handles ONLY the IPFS upload.
   */
  onUrlChange: (url: string) => void;
  /** When set, uploaded files are cropped per this config. Omit to upload without a crop step. */
  crop?: ImageCropConfig;
  /** When set, renders a preset gallery in the selection view. */
  presets?: ImagePresetGroup;
  /** When true, renders a "Remove" button in the selection view that fires `onUrlChange('')`. */
  showRemove?: boolean;
  /** Dialog title shown in the selection view (already translated). */
  title: string;
  /** Dialog title shown in the crop view (already translated). Required when `crop` is set. */
  cropTitle?: string;
  /** Toast/announcer success message (already translated). */
  successMessage: string;
  /** Optional ARIA prefix for preset items (already translated). E.g. `Avatar` → `Avatar 1`, `Avatar 2`. */
  presetAriaPrefix?: string;
  /**
   * When set, the modal ingests this `File` immediately on open — used by drop-zone
   * triggers that capture a dragged file before opening the modal. The file is
   * ingested exactly once per `open=true` transition; passing a new `File` while
   * already open does NOT re-ingest (callers must close + reopen).
   */
  initialFile?: File;
  /**
   * How the post-crop preview is rendered:
   * - `'lib'` (default): dithered through the pattern-studio lib so the
   *   user sees the formatted artefact every visitor will see (avatars,
   *   profile covers).
   * - `'raw'`: the cropped blob is shown directly. Used for surfaces that
   *   bypass the lib at display time (market images, outcome images per
   *   the 2026-05-05 carve-out).
   *
   * When `showRasteriseToggle` is `true`, this acts as the *initial* state
   * of the user-controllable toggle. Otherwise it's static.
   */
  previewMode?: 'lib' | 'raw';
  /**
   * When `true`, render a "Dithered / Original" segmented control above
   * the post-crop preview so the user can choose how the avatar will
   * render. The picked mode is reported via `onPreferencesChange` on
   * confirm and the parent persists it on `users.avatar_preferences`.
   *
   * Avatar uploads only — hero, market cover, outcome image, and proposal
   * cover surfaces leave this `false` (the default).
   */
  showRasteriseToggle?: boolean;
  /**
   * Sibling callback to `onUrlChange`. Fired on confirm with the chosen
   * preferences when `showRasteriseToggle` is `true`. The parent should
   * persist these onto `users.avatar_preferences` in the same SDK
   * mutation that writes the new `avatarUrl`.
   *
   * Modes:
   *   - `'pattern'` — dithered through pattern-studio (existing default)
   *   - `'original'` — raw cropped upload (existing alternative)
   *   - `'morfian'` — fal.ai/nano-banana/edit restyled the photo into a
   *     Pixar-style Morfian. Only emitted when `kind === 'avatar'` and the
   *     user picks the Morfianate tab.
   *
   * `rasterise` is the legacy boolean kept for backwards compatibility —
   * `pattern` ↔ `true`, the other two ↔ `false`.
   */
  onPreferencesChange?: (preferences: {
    rasterise: boolean;
    mode: 'pattern' | 'original' | 'morfian';
  }) => void;
  /**
   * Avatar / hero discriminator. When `'avatar'`, the post-crop preview
   * exposes a Morfianate tab that feeds the cropped photo through the
   * fal.ai image-to-image pipeline before persisting. Hero / launchpad
   * surfaces leave this undefined and never see Morfianate.
   */
  kind?: 'avatar' | 'hero';
}
type ModalView = 'select' | 'crop';
type CropStep = 1 | 2;
const INITIAL_CROP_DEFAULT: Crop = {
  unit: '%',
  x: 10,
  y: 10,
  width: 80,
  height: 80
};
interface CropStepIndicatorProps {
  currentStep: CropStep;
  totalSteps: number;
}
function CropStepIndicator(t0) {
  const $ = _c(5);
  const {
    currentStep,
    totalSteps
  } = t0;
  let t1;
  if ($[0] !== currentStep || $[1] !== totalSteps) {
    let t2;
    if ($[3] !== currentStep) {
      t2 = (_, i) => <div key={`crop-step-${String(i)}`} className={cn("h-1.5 flex-1 rounded-full transition-colors", i < currentStep ? "bg-foreground-primary" : "bg-background-tertiary")} />;
      $[3] = currentStep;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = <div className="flex gap-1" aria-hidden="true">{Array.from({
        length: totalSteps
      }, t2)}</div>;
    $[0] = currentStep;
    $[1] = totalSteps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
interface CropModalController {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crop: ImageCropConfig | undefined;
  presets: ImagePresetGroup | undefined;
  showRemove: boolean | undefined;
  title: string;
  cropTitle: string | undefined;
  presetAriaPrefix: string | undefined;
  showRasteriseToggle: boolean;
  kind: 'avatar' | 'hero' | undefined;
  currentUrl: string | null;
  t: ReturnType<typeof useTranslations>;
  tAvatar: ReturnType<typeof useTranslations>;
  view: ModalView;
  cropStep: CropStep;
  setCropStep: Dispatch<SetStateAction<CropStep>>;
  selected: string | null;
  setSelected: Dispatch<SetStateAction<string | null>>;
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  isSaving: boolean;
  isUploading: boolean;
  dragOver: boolean;
  setDragOver: Dispatch<SetStateAction<boolean>>;
  selectedMode: 'lib' | 'raw' | 'morfian';
  setSelectedMode: Dispatch<SetStateAction<'lib' | 'raw' | 'morfian'>>;
  morfianatePreviewUrl: string | null;
  morfianatePreviewNsfw: ReturnType<typeof useNsfwStatusByUrl>;
  morfianateLoading: boolean;
  morfianateError: string | null;
  quota: ReturnType<typeof useGetImagesQuota>;
  imageSrc: string | null;
  cropRegion: Crop;
  setCropRegion: Dispatch<SetStateAction<Crop>>;
  completedCrop: PixelCrop | undefined;
  setCompletedCrop: Dispatch<SetStateAction<PixelCrop | undefined>>;
  previewSrc: string | null;
  previewLoading: boolean;
  previewNsfw: ReturnType<typeof useNsfwStatusByUrl>;
  previewPalette: PaletteName;
  previewSeed: number;
  ingestFile: (file: File) => (() => void) | undefined;
  handleImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  updatePreviewBlob: (pixelCrop: PixelCrop) => Promise<void>;
  triggerMorfianate: () => void;
  handleCropConfirm: () => Promise<void>;
  handleCropCancel: () => void;
  handleConfirmPreset: () => Promise<void>;
  handleRemove: () => Promise<void>;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
}
function readBlobAsDataUrl(blob: Blob, reader: FileReader): Promise<string> {
  return new Promise((resolve, reject) => {
    reader.onload = (): void => {
      if (typeof reader.result === 'string') resolve(reader.result);else reject(new DOMException('Image reader returned no data URL', 'EncodingError'));
    };
    reader.onerror = (): void => {
      reject(reader.error ?? new DOMException('Image read failed', 'NotReadableError'));
    };
    reader.onabort = (): void => {
      reject(new DOMException('Image read aborted', 'AbortError'));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * All of the crop modal's state, refs, handlers, and effects (initial-file
 * ingest + deferred reset-on-close + morfianate-abort cleanup). Kept as a hook
 * in this file (not a separate module) so the component's intentional patterns
 * keep their file-keyed verified-false-positive exemptions (the raw <img> for
 * ReactCrop, the selected/selectedMode derived-from-prop state, the
 * post-await staleness guard, prefer-useReducer, and the controlled-modal
 * cascade/parent-callback effects).
 */
function useCropModal(props: CropModalProps): CropModalController {
  const {
    open,
    onOpenChange,
    onPendingUpload,
    userId,
    currentUrl,
    onUrlChange,
    crop,
    presets,
    showRemove,
    title,
    cropTitle,
    successMessage,
    presetAriaPrefix,
    initialFile,
    previewMode = 'lib',
    showRasteriseToggle = false,
    onPreferencesChange,
    kind
  } = props;
  const t = useTranslations('image_upload');
  const tAvatar = useTranslations('avatar_picker');
  const {
    announce
  } = useAnnouncer();
  const trackUpload = useUploadTrackingStore(s => s.track);
  const [view, setView] = useState<ModalView>('select');
  const [cropStep, setCropStep] = useState<CropStep>(1);
  const [selected, setSelected] = useState<string | null>(currentUrl);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);

  // User-controllable preview render mode. Initialised from `previewMode`
  // and only mutable when `showRasteriseToggle` is true. The picked value
  // is reported via `onPreferencesChange` on confirm so the parent can
  // persist it onto `users.avatar_preferences.{rasterise, mode}`.
  //
  // Three modes:
  //   - `'lib'`: dithered through pattern-studio (legacy default)
  //   - `'raw'`: passthrough of the cropped upload
  //   - `'morfian'`: cropped upload restyled via fal.ai/nano-banana/edit.
  //     Only available when `kind === 'avatar'`.
  const [selectedMode, setSelectedMode] = useState<'lib' | 'raw' | 'morfian'>(
  // Avatars retired the dithered 'lib' (Pattern) mode, so its tab is no longer
  // rendered for kind==='avatar'. A 'lib' previewMode would then leave no tab
  // selected and fall through to the dither render — making the preview not
  // match the crop and the "Original" tab look inert. Clamp avatars to 'raw'
  // (Original) so the preview matches and Original reads as selected (MOR-721).
  kind === 'avatar' && previewMode === 'lib' ? 'raw' : previewMode);
  // Cached Morfianate output URL — populated when the user previews the
  // Morfianate tab so flipping back to it doesn't re-spend a quota call.
  // Keyed (loosely) by the cropped blob URL so a re-crop invalidates it.
  const [morfianatePreviewUrl, setMorfianatePreviewUrl] = useState<string | null>(null);
  const morfianatePreviewNsfw = useNsfwStatusByUrl(morfianatePreviewUrl);
  const [morfianateLoading, setMorfianateLoading] = useState<boolean>(false);
  const [morfianateError, setMorfianateError] = useState<string | null>(null);
  const morfianateMutation = useMorfianateImage();
  const uploadMutation = useUploadImage();
  const morfianateAbortRef = useRef<AbortController | null>(null);
  const morfianateForBlobRef = useRef<string | null>(null);
  // Live daily-quota state — refetched after a successful Morfianate so the
  // small caption above the CTA reflects the post-increment counter.
  const quotaEnabled = shouldFetchImageQuota({
    open,
    generationEnabled: kind === 'avatar' && checkGate('morfian_avatar')
  });
  const quota = useGetImagesQuota({
    fields: ['used', 'daily', 'remaining', 'resetsAt']
  }, {
    enabled: quotaEnabled
  });

  // Crop state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [cropRegion, setCropRegion] = useState<Crop>(INITIAL_CROP_DEFAULT);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | undefined>(undefined);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewLoadingGeneration, setPreviewLoadingGeneration] = useState<number | null>(null);
  const previewNsfw = useNsfwStatusByUrl(previewSrc);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const ingestedInitialFileRef = useRef<File | null>(null);
  const ingestGenerationRef = useRef(0);
  const activeSourceReaderRef = useRef<FileReader | null>(null);
  const previewGenRef = useRef<number>(0);
  const activePreviewReaderRef = useRef<FileReader | null>(null);
  // The cropped Blob is generated while the cropper `<img>` is still
  // mounted (step 1) — caching it here lets the step-2 confirm reuse the
  // SAME bytes as the preview, instead of re-running `getCroppedBlob`
  // against an unmounted img whose `clientWidth/Height` are 0.
  const cachedCroppedBlobRef = useRef<Blob | null>(null);

  // Stable palette + seed for the post-crop preview. Both derive from
  // `userId` (when supplied) so the preview renders identically to what
  // every visitor will see after upload. When `userId` is omitted the
  // lib's default palette is used — adequate for launchpad / cover
  // uploads that aren't user-identity-bound.
  const previewSeedSource: string = userId ?? '';
  const previewPalette = pickBrandBluePalette(previewSeedSource);
  const previewSeed = hashStringToSeed(previewSeedSource);

  // Moderation polling has been moved out of the modal entirely. After
  // Pinata upload succeeds the modal stashes (url, sha256) in the
  // upload-tracking store and closes immediately. Surfaces that render
  // the URL (proposal wizard, market row, etc.) call
  // `useNsfwStatusByUrl(url)` to apply the blur overlay if the verdict
  // lands as flagged. The cron sweeper handles late verdicts server-side.

  function ingestFile(file: File): (() => void) | undefined {
    const generation = ++ingestGenerationRef.current;
    const previousReader = activeSourceReaderRef.current;
    activeSourceReaderRef.current = null;
    if (previousReader?.readyState === FileReader.LOADING) previousReader.abort();
    const validation = validateImageUpload(file);
    if (validation === 'invalid_type') {
      setError(t('error_invalid_type'));
      return undefined;
    }
    if (validation === 'too_large') {
      setError(t('error_too_large', {
        maxMB: IMAGE_UPLOAD_MAX_MB
      }));
      return undefined;
    }
    setError(null);
    if (crop !== undefined) {
      // Open the crop view — the user adjusts the region before upload.
      const reader = new FileReader();
      activeSourceReaderRef.current = reader;
      reader.onload = (): void => {
        if (generation !== ingestGenerationRef.current || typeof reader.result !== 'string') return;
        activeSourceReaderRef.current = null;
        const dataUrl = reader.result;
        setImageSrc(dataUrl);
        setCropRegion(INITIAL_CROP_DEFAULT);
        setCompletedCrop(undefined);
        cachedCroppedBlobRef.current = null;
        setCropStep(1);
        setView('crop');
      };
      reader.onerror = (): void => {
        if (generation !== ingestGenerationRef.current) return;
        activeSourceReaderRef.current = null;
        const err = reader.error;
        logger.error('Image crop source read failed', {
          error: err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200)
        });
        setError(t('upload_error'));
      };
      reader.readAsDataURL(file);
      return (): void => {
        if (generation === ingestGenerationRef.current) ingestGenerationRef.current++;
        if (reader.readyState === FileReader.LOADING) reader.abort();
        if (activeSourceReaderRef.current === reader) activeSourceReaderRef.current = null;
      };
    }

    // No crop step — upload directly.
    void uploadFile(file);
    return undefined;
  }
  async function uploadFile(file_0: File): Promise<void> {
    // Non-crop path: no blob handle ahead of upload, so we can't fire an
    // optimistic blob URL. Show the spinner during Pinata, close modal on
    // success, propagate the IPFS URL via `onUrlChange`. Most callers use
    // the crop path (which is optimistic), so this branch is rare.
    setIsUploading(true);
    setError(null);
    await runWithCleanup(async () => {
      try {
        // ALLOW-UNKNOWN: SDK v2 mutation return type is generic `unknown`; cast to the documented contract.
        const result = (await uploadMutation.mutateAsync({
          file: file_0
        })) as {
          url: string;
          sha256: string;
        };
        trackUpload(result.url, result.sha256);
        announce(successMessage);
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD_SUCCESS,
          message: successMessage,
          variant: 'success'
        });
        setSelected(result.url);
        onUrlChange(result.url);
        onOpenChange(false);
      } catch (err_0: unknown) {
        const message = err_0 instanceof Error ? err_0.message : String(err_0);
        logger.error('Image upload failed', {
          error: message.slice(0, 200)
        });
        setError(message);
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD_ERROR,
          message: t('upload_error'),
          variant: 'error'
        });
      }
    }, () => {
      setIsUploading(false);
    });
  }
  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement>): void {
    if (crop === undefined) return;
    const img = e.currentTarget;
    imgRef.current = img;
    // Use clientWidth/clientHeight so the pre-fill matches ReactCrop's
    // measurement basis — `img.width`/`height` read the HTML attribute and
    // can disagree with the rendered CSS size, producing a crop that drifts
    // off the visible region.
    const width = img.clientWidth || img.width;
    const height = img.clientHeight || img.height;
    // Compute the largest aspect-respecting box that FITS INSIDE the
    // displayed image, then center it. We do this in pixels (not %) because
    // `makeAspectCrop({ unit: '%' })` is allowed to overflow the media when
    // the crop aspect doesn't match the image aspect — its returned width%
    // and height% are taken against different reference dimensions, so for
    // a square crop on a landscape image the height% can exceed 100% and
    // the centered y becomes negative. Pre-filling completedCrop from those
    // overflowing % values then drawing through `getCroppedBlob` reads
    // outside the image bounds and produces a preview cropped toward the
    // wrong edge. Computing pixel coords directly avoids the whole hazard.
    const fitScale = 0.9; // leave a small inset so the handles are reachable
    const imgAspect = width / height;
    const cropAspect = crop.aspect;
    let cropW: number;
    let cropH: number;
    if (cropAspect >= imgAspect) {
      cropW = width * fitScale;
      cropH = cropW / cropAspect;
    } else {
      cropH = height * fitScale;
      cropW = cropH * cropAspect;
    }
    const pxCrop: PixelCrop = {
      unit: 'px',
      x: (width - cropW) / 2,
      y: (height - cropH) / 2,
      width: cropW,
      height: cropH
    };
    setCropRegion(pxCrop);
    setCompletedCrop(pxCrop);
    void updatePreviewBlob(pxCrop);
  }
  async function handleCropConfirm(): Promise<void> {
    if (completedCrop === undefined || crop === undefined) return;

    // Morfianate path — the chosen image is the IPFS URL the backend already
    // pinned during the preview. Skip the second IPFS upload entirely and
    // hand the URL straight to the parent. Falls through to the regular path
    // when the Morfianate result hasn't landed yet (e.g. user clicked
    // Confirm while still loading) — in that case we wait by short-circuit.
    if (selectedMode === 'morfian') {
      if (morfianatePreviewUrl === null) {
        // Still pending or errored. Show the same generic upload error toast
        // and bail; the user can wait for the preview to settle (or re-pick
        // a tab) before confirming again.
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD_ERROR,
          message: morfianateError ?? t('morfianate_running'),
          variant: 'error'
        });
        return;
      }
      // Mirror the launch sequence used by the upload path: announce + close
      // + emit preferences before the URL so the parent's PATCH carries
      // `mode: 'morfian'` with the legacy raster flag disabled.
      announce(successMessage);
      showToast({
        id: TOAST_IDS.IMAGE_UPLOAD,
        message: successMessage,
        variant: 'success'
      });
      setSelected(morfianatePreviewUrl);
      onOpenChange(false);
      if (showRasteriseToggle) {
        // ALLOW-RASTER-LITERAL: Morfian-mode confirm path — disabling raster is intrinsic to selecting a Morfian (no user preference can override).
        onPreferencesChange?.({
          rasterise: false,
          mode: 'morfian'
        });
      }
      onUrlChange(morfianatePreviewUrl);
      return;
    }

    // Acquire the cropped blob first. This is local + sync-ish (~50 ms).
    // Prefer the cached blob from step-1 — see the cachedCroppedBlobRef
    // comment for the unmounted-cropper hazard.
    let blob: Blob | null = cachedCroppedBlobRef.current;
    if (blob === null) {
      if (imgRef.current === null) return;
      try {
        blob = await getCroppedBlob(imgRef.current, completedCrop, crop.outputSize);
      } catch (err_1: unknown) {
        const message_0 = err_1 instanceof Error ? err_1.message : String(err_1);
        logger.error('Image crop failed', {
          error: message_0.slice(0, 200)
        });
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD_ERROR,
          message: t('upload_error'),
          variant: 'error'
        });
        return;
      }
    }

    // Optimistic display: hand the parent a `blob:` URL so the wizard /
    // surrounding modal advances NOW. Pinata runs fire-and-forget below;
    // when it resolves we fire `onUrlChange` with the real IPFS URL and
    // the parent swaps. Perceived latency ≈ 0.
    const blobUrl = URL.createObjectURL(blob);
    onPendingUpload?.(blobUrl);
    announce(t('uploading'));
    showToast({
      id: TOAST_IDS.IMAGE_UPLOAD,
      message: t('uploading'),
      variant: 'loading'
    });

    // Close NOW. The picker may unmount before Pinata resolves; the
    // captured `onUrlChange` runs against the parent's current closure.
    setSelected(blobUrl);
    onOpenChange(false);
    const stem = crop.fileNameStem ?? 'image';
    const croppedFile = new File([blob], `${stem}.png`, {
      type: 'image/png'
    });
    void runWithCleanup(async () => {
      try {
        // ALLOW-UNKNOWN: SDK v2 mutation return type is generic `unknown`; cast to the documented contract.
        const result_0 = (await uploadMutation.mutateAsync({
          file: croppedFile
        })) as {
          url: string;
          sha256: string;
        };
        trackUpload(result_0.url, result_0.sha256);
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD,
          message: successMessage,
          variant: 'success'
        });
        // Fire `onPreferencesChange` BEFORE `onUrlChange` so the parent can
        // capture the user's choice into a ref before its async URL handler
        // runs — otherwise React batching means the rasterise flag arrives
        // after the SDK PATCH has already gone out with `avatarUrl` only.
        // `lib` = dithered through the pattern-studio rasteriser;
        // `raw` = passthrough of the uploaded image.
        if (showRasteriseToggle) {
          onPreferencesChange?.({
            rasterise: selectedMode === 'lib',
            mode: selectedMode === 'lib' ? 'pattern' : 'original'
          });
        }
        onUrlChange(result_0.url);
      } catch (err_2: unknown) {
        const message_1 = err_2 instanceof Error ? err_2.message : String(err_2);
        logger.error('Image crop+upload failed', {
          error: message_1.slice(0, 200)
        });
        showToast({
          id: TOAST_IDS.IMAGE_UPLOAD,
          message: t('upload_error'),
          variant: 'error'
        });
        // Empty URL signals the parent to roll back the optimistic blob URL.
        onUrlChange('');
      }
    }, () => {
      // Free on next tick — parent has already swapped to the IPFS URL.
      setTimeout((): void => {
        URL.revokeObjectURL(blobUrl);
      }, 0);
    });
  }
  function handleCropCancel(): void {
    // Preset-gallery flow: fall back to the gallery so the user can pick a
    // different preset. Cleanup happens immediately because the modal stays
    // open and we want a clean slate for the next selection.
    if (presets !== undefined) {
      setImageSrc(null);
      setPreviewSrc(null);
      setCompletedCrop(undefined);
      cachedCroppedBlobRef.current = null;
      setCropStep(1);
      setView('select');
      return;
    }
    // No-presets flow (identity-picker, image-upload-dropzone): close the modal
    // entirely so the parent's tab strip / dropzone takes focus back.
    // We DO NOT clear `view`/`imageSrc` synchronously — Radix keeps the
    // content mounted for ~200ms during the close animation, and clearing
    // mid-animation flips the conditional render from crop-view → select-view
    // and flashes the bare dropzone. The cleanup effect below runs once
    // `open` flips to false (after Radix unmounts the content) so the next
    // open cycle starts clean.
    onOpenChange(false);
  }

  /**
   * Recompute the post-crop preview source whenever the crop region
   * settles. The preview feeds `<OptimizedImage entity={{ kind: 'preview' }}>`
   * so the user sees the formatted artefact (lib palette + dither) the
   * moment they finish dragging, before they confirm upload.
   */
  const updatePreviewBlob = async (pixelCrop: PixelCrop): Promise<void> => {
    if (imgRef.current === null || crop === undefined) return;
    if (pixelCrop.width <= 0 || pixelCrop.height <= 0) return;
    const img_0 = imgRef.current;
    const outputSize = crop.outputSize;
    const gen = ++previewGenRef.current;
    const previousReader_0 = activePreviewReaderRef.current;
    activePreviewReaderRef.current = null;
    if (previousReader_0?.readyState === FileReader.LOADING) previousReader_0.abort();
    setPreviewLoadingGeneration(gen);
    await runWithCleanup(async () => {
      try {
        const blob_0 = await getCroppedBlob(img_0, pixelCrop, outputSize);
        if (gen !== previewGenRef.current) {
          // Superseded by a newer crop event — drop this stale result.
          return;
        }
        cachedCroppedBlobRef.current = blob_0;
        const reader_0 = new FileReader();
        activePreviewReaderRef.current = reader_0;
        const url = await readBlobAsDataUrl(blob_0, reader_0).finally((): void => {
          if (activePreviewReaderRef.current === reader_0) activePreviewReaderRef.current = null;
        });
        if (gen !== previewGenRef.current) return;
        setPreviewSrc(url);
        // Re-cropping invalidates the cached Morfianate output. The next time
        // the user picks the Morfianate tab the call re-fires against the new
        // crop. We DON'T abort an in-flight request here; if it lands first
        // the staleness check below filters it out.
        if (morfianateForBlobRef.current !== url) {
          setMorfianatePreviewUrl(null);
          setMorfianateError(null);
        }
      } catch (err_3: unknown) {
        if (gen !== previewGenRef.current) return;
        const message_2 = err_3 instanceof Error ? err_3.message : String(err_3);
        logger.error('Image post-crop preview failed', {
          error: message_2.slice(0, 200)
        });
      }
    }, () => {
      setPreviewLoadingGeneration(current => current === gen ? null : current);
    });
  };

  /**
   * Fire a Morfianate call against the currently-cached cropped blob. Caches
   * the resulting IPFS URL on `morfianatePreviewUrl` so re-flipping the tab
   * doesn't re-spend a quota generation. Aborts any in-flight call before
   * starting a new one — re-cropping will null out the cached preview and
   * `triggerMorfianate` re-runs when the user lands back on the tab.
   */
  function triggerMorfianate(): void {
    const blob_1 = cachedCroppedBlobRef.current;
    if (blob_1 === null) return;
    const cropKey = previewSrc ?? '';
    // Already have a result for this exact crop → no-op.
    if (morfianatePreviewUrl !== null && morfianateForBlobRef.current === cropKey) return;
    if (morfianateAbortRef.current !== null) {
      morfianateAbortRef.current.abort();
    }
    const controller = new AbortController();
    morfianateAbortRef.current = controller;
    morfianateForBlobRef.current = cropKey;
    setMorfianateLoading(true);
    setMorfianateError(null);
    morfianateMutation.mutate({
      image: blob_1,
      signal: controller.signal
    }, {
      // ALLOW-UNKNOWN: SDK v2 mutation onSuccess data is generic `unknown`; cast to the documented contract.
      onSuccess: (raw: unknown): void => {
        const data = raw as {
          imageUrl: string;
          sourceImageUrl: string;
        };
        if (morfianateAbortRef.current !== controller) return;
        morfianateAbortRef.current = null;
        setMorfianatePreviewUrl(data.imageUrl);
        setMorfianateLoading(false);
        // Quota updates declaratively via the manifest's `add: { used: 1, remaining: -1 }`
        // recipe — no explicit refetch needed.
      },
      // ALLOW-UNKNOWN: TanStack onError carries any thrown value — narrowed inside.
      onError: (err_4: unknown): void => {
        if (err_4 instanceof DOMException && err_4.name === 'AbortError') {
          if (morfianateAbortRef.current === controller) {
            morfianateAbortRef.current = null;
          }
          return;
        }
        if (morfianateAbortRef.current !== controller) return;
        morfianateAbortRef.current = null;
        const message_3 = err_4 instanceof Error ? err_4.message : String(err_4);
        logger.error('crop-modal morfianate failed', {
          error: message_3.slice(0, 200)
        });
        setMorfianateError(t('morfianate_error'));
        setMorfianateLoading(false);
      }
    });
  }

  // Cleanup any in-flight Morfianate request on unmount so a stale response
  // doesn't try to setState on an unmounted tree.
  useEffect((): (() => void) => {
    return (): void => {
      if (morfianateAbortRef.current !== null) {
        morfianateAbortRef.current.abort();
        morfianateAbortRef.current = null;
      }
    };
  }, []);
  async function handleConfirmPreset(): Promise<void> {
    if (selected === null) return;
    setIsSaving(true);
    setError(null);
    await runWithCleanup(async () => {
      onUrlChange(selected);
      announce(successMessage);
      showToast({
        id: TOAST_IDS.IMAGE_UPLOAD_SUCCESS,
        message: successMessage,
        variant: 'success'
      });
      onOpenChange(false);
    }, () => {
      setIsSaving(false);
    });
  }
  async function handleRemove(): Promise<void> {
    setIsSaving(true);
    setError(null);
    await runWithCleanup(async () => {
      onUrlChange('');
      announce(successMessage);
      showToast({
        id: TOAST_IDS.IMAGE_UPLOAD_SUCCESS,
        message: successMessage,
        variant: 'success'
      });
      onOpenChange(false);
    }, () => {
      setIsSaving(false);
    });
  }
  function handleDrop(e_0: React.DragEvent<HTMLDivElement>): void {
    e_0.preventDefault();
    setDragOver(false);
    const file_1 = e_0.dataTransfer.files[0];
    if (file_1 !== undefined) {
      void ingestFile(file_1);
    }
  }

  // When the modal opens with an `initialFile` (from a drop-zone trigger), ingest
  // it exactly once per open cycle. Reset the guard when the modal closes so a
  // subsequent open with a new pending file ingests again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: REASON: ingestFile is stable per render and reads `crop` via closure; adding it to deps would re-fire every render. The guard ref ensures single-ingestion per open transition.
  useEffect((): (() => void) | undefined => {
    if (!open) {
      ingestedInitialFileRef.current = null;
      return undefined;
    }
    if (initialFile === undefined) return undefined;
    if (ingestedInitialFileRef.current === initialFile) return undefined;
    ingestedInitialFileRef.current = initialFile;
    return ingestFile(initialFile);
  }, [open, initialFile]);

  // Deferred cleanup-on-close. Radix Dialog keeps the content mounted for
  // ~200ms during its exit animation. If we clear `view`/`imageSrc` the
  // moment `open` flips to false, the conditional render swaps the crop
  // view for the select view mid-animation — two different DialogContent
  // subtrees crossing inside Radix, which surfaces as a visible flash of
  // the bare-dropzone select view. Defer the state reset past the
  // animation; the crop view fades out cleanly.
  useEffect((): (() => void) | undefined => {
    if (open) return undefined;
    ingestGenerationRef.current++;
    const sourceReader = activeSourceReaderRef.current;
    activeSourceReaderRef.current = null;
    if (sourceReader?.readyState === FileReader.LOADING) sourceReader.abort();
    previewGenRef.current++;
    const previewReader = activePreviewReaderRef.current;
    activePreviewReaderRef.current = null;
    if (previewReader?.readyState === FileReader.LOADING) previewReader.abort();
    const timeoutId = setTimeout((): void => {
      setImageSrc(null);
      setPreviewSrc(null);
      setCompletedCrop(undefined);
      cachedCroppedBlobRef.current = null;
      setCropStep(1);
      setView('select');
      setMorfianatePreviewUrl(null);
      setMorfianateError(null);
    }, 300);
    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [open]);
  useEffect(() => (): void => {
    ingestGenerationRef.current++;
    const sourceReader_0 = activeSourceReaderRef.current;
    activeSourceReaderRef.current = null;
    if (sourceReader_0?.readyState === FileReader.LOADING) sourceReader_0.abort();
    previewGenRef.current++;
    const previewReader_0 = activePreviewReaderRef.current;
    activePreviewReaderRef.current = null;
    if (previewReader_0?.readyState === FileReader.LOADING) previewReader_0.abort();
  }, []);
  return {
    open,
    onOpenChange,
    crop,
    presets,
    showRemove,
    title,
    cropTitle,
    presetAriaPrefix,
    showRasteriseToggle,
    kind,
    currentUrl,
    t,
    tAvatar,
    view,
    cropStep,
    setCropStep,
    selected,
    setSelected,
    error,
    setError,
    isSaving,
    isUploading,
    dragOver,
    setDragOver,
    selectedMode,
    setSelectedMode,
    morfianatePreviewUrl,
    morfianatePreviewNsfw,
    morfianateLoading,
    morfianateError,
    quota,
    imageSrc,
    cropRegion,
    setCropRegion,
    completedCrop,
    setCompletedCrop,
    previewSrc,
    previewLoading: open && previewLoadingGeneration !== null,
    previewNsfw,
    previewPalette,
    previewSeed,
    ingestFile,
    handleImageLoad,
    updatePreviewBlob,
    triggerMorfianate,
    handleCropConfirm,
    handleCropCancel,
    handleConfirmPreset,
    handleRemove,
    handleDrop
  };
}

/**
 * `<CropModal>` — single confirm/crop/upload surface for every image upload
 * in the app. Handles preset selection, drag-and-drop, file validation,
 * optional cropping, IPFS upload, and removal. Consumers control persistence
 * via `onUrlChange`.
 *
 * See `frontend/src/components/images/index.ts` for the consolidated entry
 * points (avatar, hero, launchpad cover) — those are thin wrappers around
 * this component with surface-specific config.
 */
export function CropModal(props) {
  const $ = _c(4);
  const c = useCropModal(props);
  if (c.view === "crop" && c.imageSrc !== null && c.crop !== undefined) {
    let t0;
    if ($[0] !== c) {
      t0 = <CropView c={c} crop={c.crop} imageSrc={c.imageSrc} />;
      $[0] = c;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== c) {
    t0 = <SelectView c={c} />;
    $[2] = c;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}

/**
 * Two-step crop + formatted-preview view. Each step gets the full dialog real
 * estate so the cropper and the formatted preview both render comfortably.
 */
function CropView(t0) {
  const $ = _c(73);
  const {
    c,
    crop,
    imageSrc
  } = t0;
  const {
    t,
    tAvatar,
    cropStep
  } = c;
  let t1;
  if ($[0] !== c.cropTitle || $[1] !== c.title || $[2] !== cropStep || $[3] !== t) {
    t1 = cropStep === 1 ? c.cropTitle ?? c.title : t("preview_step_title");
    $[0] = c.cropTitle;
    $[1] = c.title;
    $[2] = cropStep;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const stepTitle = t1;
  const t2 = c.cropRegion;
  let t3;
  let t4;
  if ($[5] !== c) {
    t3 = region => {
      c.setCropRegion(region);
    };
    t4 = region_0 => {
      c.setCompletedCrop(region_0);
      c.updatePreviewBlob(region_0);
    };
    $[5] = c;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  const t5 = crop.aspect;
  const t6 = crop.circular === true;
  let t7;
  if ($[8] !== t) {
    t7 = t("preview_alt");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== c.handleImageLoad || $[11] !== imageSrc || $[12] !== t7) {
    t8 = <img src={imageSrc} alt={t7} onLoad={c.handleImageLoad} className="max-h-96 w-auto" />;
    $[10] = c.handleImageLoad;
    $[11] = imageSrc;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== c.cropRegion || $[15] !== crop.aspect || $[16] !== t3 || $[17] !== t4 || $[18] !== t6 || $[19] !== t8) {
    t9 = <div className="flex items-center justify-center overflow-hidden rounded-card bg-background-secondary"><ReactCrop crop={t2} onChange={t3} onComplete={t4} aspect={t5} circularCrop={t6} className="max-h-96">{t8}</ReactCrop></div>;
    $[14] = c.cropRegion;
    $[15] = crop.aspect;
    $[16] = t3;
    $[17] = t4;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const cropPanel = t9;
  let t10;
  if ($[21] !== c.quota || $[22] !== c.selectedMode || $[23] !== tAvatar) {
    t10 = c.selectedMode === "morfian" ? c.quota.data != null ? c.quota.data.remaining <= 0 ? <p className="text-center text-caption text-foreground-tertiary"><span>{tAvatar("morfianate_duration_caption", {
          seconds: 20
        })}</span><span> • </span><span className="text-feedback-error-content">{tAvatar("morfian_quota_exceeded")}</span></p> : <p className="text-center text-caption text-foreground-tertiary"><span>{tAvatar("morfianate_duration_caption", {
          seconds: 20
        })}</span><span> • </span><span className={cn(c.quota.data.remaining <= 2 ? "text-feedback-error-content" : undefined)}>{tAvatar("morfian_quota_caption", {
          remaining: c.quota.data.remaining,
          daily: c.quota.data.daily
        })}</span></p> : <p className="text-center text-caption text-foreground-tertiary">{tAvatar("morfianate_duration_caption", {
        seconds: 20
      })}</p> : null;
    $[21] = c.quota;
    $[22] = c.selectedMode;
    $[23] = tAvatar;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const morfianateCaptionRow = t10;
  let t11;
  if ($[25] !== c || $[26] !== cropStep) {
    t11 = () => {
      if (cropStep === 1) {
        c.handleCropCancel();
        return;
      }
      c.setCropStep(1);
      c.setError(null);
    };
    $[25] = c;
    $[26] = cropStep;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== cropStep || $[29] !== t) {
    t12 = cropStep === 1 ? t("cancel") : t("back");
    $[28] = cropStep;
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== c.isUploading || $[32] !== t11 || $[33] !== t12) {
    t13 = <AnimatedButton variant="secondary" className="flex-1" onClick={t11} disabled={c.isUploading}>{t12}</AnimatedButton>;
    $[31] = c.isUploading;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== c || $[36] !== cropStep || $[37] !== t) {
    t14 = cropStep === 1 ? <AnimatedButton className="flex-1" disabled={c.completedCrop === undefined || c.previewLoading} loading={c.previewLoading} onClick={() => {
      c.setCropStep(2);
      c.setError(null);
    }}>{t("next")}</AnimatedButton> : <AnimatedButton className="flex-1" disabled={c.completedCrop === undefined || c.isUploading} loading={c.isUploading} onClick={c.handleCropConfirm}>{c.isUploading ? t("saving") : t("confirm")}</AnimatedButton>;
    $[35] = c;
    $[36] = cropStep;
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14) {
    t15 = <ModalFooter>{t13}{t14}</ModalFooter>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const cropFooter = t15;
  let t16;
  if ($[42] !== stepTitle) {
    t16 = <span className="text-heading-md">{stepTitle}</span>;
    $[42] = stepTitle;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== cropStep) {
    t17 = <CropStepIndicator currentStep={cropStep} totalSteps={2} />;
    $[44] = cropStep;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t16 || $[47] !== t17) {
    t18 = <div className="flex flex-col gap-3">{t16}{t17}</div>;
    $[46] = t16;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== c.error || $[50] !== c.isUploading || $[51] !== c.previewLoading || $[52] !== cropStep) {
    t19 = [cropStep, c.error, c.previewLoading, c.isUploading];
    $[49] = c.error;
    $[50] = c.isUploading;
    $[51] = c.previewLoading;
    $[52] = cropStep;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== c || $[55] !== crop || $[56] !== cropPanel || $[57] !== cropStep) {
    t20 = cropStep === 1 ? cropPanel : <CropPreviewPanel c={c} crop={crop} />;
    $[54] = c;
    $[55] = crop;
    $[56] = cropPanel;
    $[57] = cropStep;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== c.error) {
    t21 = c.error !== null && <p role="alert" className="text-body text-feedback-error-content">{c.error}</p>;
    $[59] = c.error;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  const t22 = cropStep === 2 ? morfianateCaptionRow : null;
  let t23;
  if ($[61] !== t19 || $[62] !== t20 || $[63] !== t21 || $[64] !== t22) {
    t23 = <MotionAnimatedAutoHeight layoutDependencies={t19} innerClassName="flex flex-col gap-4">{t20}{t21}{t22}</MotionAnimatedAutoHeight>;
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== c.onOpenChange || $[67] !== c.open || $[68] !== cropFooter || $[69] !== stepTitle || $[70] !== t18 || $[71] !== t23) {
    t24 = <SilkSheet open={c.open} onOpenChange={c.onOpenChange} variant="detached" size="default" title={stepTitle} header={t18} footer={cropFooter}>{t23}</SilkSheet>;
    $[66] = c.onOpenChange;
    $[67] = c.open;
    $[68] = cropFooter;
    $[69] = stepTitle;
    $[70] = t18;
    $[71] = t23;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  return t24;
}

/**
 * Step-2 formatted preview — the segmented Original/Morfianate/Pattern toggle
 * plus the rendered post-crop artefact (lib dither, raw, or Morfianate result).
 */
function CropPreviewPanel(t0) {
  const $ = _c(45);
  const {
    c,
    crop
  } = t0;
  const {
    t,
    tAvatar
  } = c;
  const isSquareNonCircular = crop.aspect === 1 && crop.circular !== true;
  let t1;
  if ($[0] !== c.kind) {
    t1 = c.kind === "avatar" && checkGate("morfian_avatar");
    $[0] = c.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const morfianateEnabled = t1;
  let t2;
  if ($[2] !== t) {
    t2 = t("preview_original");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      value: "raw",
      label: t2,
      icon: Camera01Icon
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== morfianateEnabled || $[7] !== t) {
    t4 = morfianateEnabled ? [{
      value: "morfian",
      label: t("preview_morfianate"),
      icon: SparklesIcon
    }] as const : [];
    $[6] = morfianateEnabled;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== c.kind || $[10] !== t) {
    t5 = c.kind !== "avatar" ? [{
      value: "lib",
      label: t("preview_pattern"),
      icon: PaintBoardIcon
    }] as const : [];
    $[9] = c.kind;
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = [t3, ...t4, ...t5];
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const previewTabsItems = t6;
  let t7;
  if ($[16] !== c || $[17] !== previewTabsItems) {
    t7 = c.showRasteriseToggle ? <div className="w-full"><AppTabs value={c.selectedMode} onValueChange={v => {
        if (v === "lib" || v === "raw" || v === "morfian") {
          c.setSelectedMode(v);
        }
      }} items={previewTabsItems} /></div> : null;
    $[16] = c;
    $[17] = previewTabsItems;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = crop.circular === true ? "size-48 rounded-full sm:size-56" : isSquareNonCircular ? "size-56 rounded-card sm:size-64" : "aspect-[3/1] w-full max-w-md rounded-card";
  let t9;
  if ($[19] !== t8) {
    t9 = cn("relative flex items-center justify-center overflow-hidden bg-background-tertiary", t8);
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== c.morfianateLoading || $[22] !== c.morfianatePreviewNsfw || $[23] !== c.morfianatePreviewUrl || $[24] !== c.previewNsfw || $[25] !== c.previewPalette || $[26] !== c.previewSeed || $[27] !== c.previewSrc || $[28] !== c.quota || $[29] !== c.selectedMode || $[30] !== c.triggerMorfianate || $[31] !== crop.circular || $[32] !== t || $[33] !== tAvatar) {
    t10 = c.previewSrc !== null ? c.selectedMode === "morfian" ? c.morfianateLoading ? <GeneratingFrame shape={crop.circular === true ? "circle" : "square"} expectedDurationMs={20000} done={false}><ImageShimmer /></GeneratingFrame> : c.morfianatePreviewUrl !== null ? <OptimizedImage variant={crop.circular === true ? "avatar" : "hero"} src={c.morfianatePreviewUrl} alt={t("post_crop_preview_alt")} {...crop.circular === true ? {
      size: 224
    } : {}} className="size-full" imageClassName="object-cover" rounded={crop.circular === true ? "rounded-full" : ""} nsfw={c.morfianatePreviewNsfw.isFlagged} preview={false} /> : <><div className="absolute inset-0 size-full overflow-hidden blur-md"><OptimizedImage variant={crop.circular === true ? "avatar" : "hero"} src={c.previewSrc} alt={t("post_crop_preview_alt")} {...crop.circular === true ? {
          size: 224
        } : {}} className="size-full" imageClassName="object-cover" rounded={crop.circular === true ? "rounded-full" : ""} nsfw={c.previewNsfw.isFlagged} preview={false} /></div><div className="absolute inset-0 flex items-center justify-center bg-surface-scrim-default p-3 text-center">{(c.quota.data?.remaining ?? c.quota.data?.daily ?? 1) <= 0 ? <span className="inline-flex items-center gap-1 rounded-full bg-background-primary px-3 py-1.5 text-caption text-foreground-primary shadow-elevation-card">{tAvatar("morfian_quota_exceeded")}</span> : <AnimatedButton type="button" prefix={SparklesIcon} onClick={c.triggerMorfianate} className="shadow-elevation-popover ring-1 ring-foreground-primary/20">{t("morfianate_cta")}</AnimatedButton>}</div></> : c.selectedMode === "raw" ? <OptimizedImage variant={crop.circular === true ? "avatar" : "hero"} src={c.previewSrc} alt={t("post_crop_preview_alt")} {...crop.circular === true ? {
      size: 224
    } : {}} className="size-full" imageClassName="object-cover" rounded={crop.circular === true ? "rounded-full" : ""} nsfw={c.previewNsfw.isFlagged} preview={false} /> : crop.circular === true ? <OptimizedImage variant="avatar" entity={{
      kind: "preview",
      sourceBlobUrl: c.previewSrc,
      paletteRef: {
        kind: "preset",
        name: c.previewPalette
      },
      seed: c.previewSeed
    }} alt={t("post_crop_preview_alt")} size={224} className="size-full" imageClassName="object-cover" rounded="rounded-full" preview={false} /> : <OptimizedImage variant="hero" src={c.previewSrc} alt={t("post_crop_preview_alt")} className="size-full" imageClassName="object-cover" rounded="" nsfw={c.previewNsfw.isFlagged} preview={false} /> : <div className="flex size-full items-center justify-center text-foreground-tertiary"><Icon icon={Loading03Icon} className="size-6 animate-spin" aria-hidden="true" /></div>;
    $[21] = c.morfianateLoading;
    $[22] = c.morfianatePreviewNsfw;
    $[23] = c.morfianatePreviewUrl;
    $[24] = c.previewNsfw;
    $[25] = c.previewPalette;
    $[26] = c.previewSeed;
    $[27] = c.previewSrc;
    $[28] = c.quota;
    $[29] = c.selectedMode;
    $[30] = c.triggerMorfianate;
    $[31] = crop.circular;
    $[32] = t;
    $[33] = tAvatar;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t9) {
    t11 = <div className={t9}>{t10}</div>;
    $[35] = t10;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== c.morfianateError || $[39] !== c.selectedMode) {
    t12 = c.selectedMode === "morfian" && c.morfianateError !== null ? <p role="alert" className="text-body text-feedback-error-content">{c.morfianateError}</p> : null;
    $[38] = c.morfianateError;
    $[39] = c.selectedMode;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t11 || $[42] !== t12 || $[43] !== t7) {
    t13 = <div className="flex flex-col items-center gap-3">{t7}{t11}{t12}</div>;
    $[41] = t11;
    $[42] = t12;
    $[43] = t7;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  return t13;
}

/** Selection view — presets + upload + drag-drop + optional remove. */
function SelectView(t0) {
  const $ = _c(61);
  const {
    c
  } = t0;
  const {
    t,
    presets,
    currentUrl,
    selected
  } = c;
  const fileInputRef = useRef(null);
  let t1;
  if ($[0] !== c) {
    t1 = function handleFileInput(e) {
      const file = e.target.files?.[0];
      if (file !== undefined) {
        c.ingestFile(file);
      }
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = "";
      }
    };
    $[0] = c;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleFileInput = t1;
  const hasSelectionFooter = presets !== undefined || c.showRemove === true && currentUrl !== null && currentUrl.length > 0;
  let t2;
  if ($[2] !== c.handleConfirmPreset || $[3] !== c.handleRemove || $[4] !== c.isSaving || $[5] !== c.isUploading || $[6] !== c.showRemove || $[7] !== currentUrl || $[8] !== hasSelectionFooter || $[9] !== presets || $[10] !== selected || $[11] !== t) {
    t2 = hasSelectionFooter ? <ModalFooter>{c.showRemove === true && currentUrl !== null && currentUrl.length > 0 ? <AnimatedButton type="button" variant="secondary" onClick={c.handleRemove} disabled={c.isSaving || c.isUploading} className="w-full sm:w-auto">{t("remove")}</AnimatedButton> : null}{presets !== undefined && <AnimatedButton type="button" onClick={c.handleConfirmPreset} disabled={selected === null || selected === currentUrl || c.isSaving || c.isUploading} loading={c.isSaving} className="w-full sm:w-auto">{c.isSaving ? t("saving") : t("confirm")}</AnimatedButton>}</ModalFooter> : undefined;
    $[2] = c.handleConfirmPreset;
    $[3] = c.handleRemove;
    $[4] = c.isSaving;
    $[5] = c.isUploading;
    $[6] = c.showRemove;
    $[7] = currentUrl;
    $[8] = hasSelectionFooter;
    $[9] = presets;
    $[10] = selected;
    $[11] = t;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const selectionFooter = t2;
  const t3 = c.open;
  const t4 = c.onOpenChange;
  const t5 = c.title;
  let t6;
  if ($[13] !== c || $[14] !== presets || $[15] !== selected || $[16] !== t) {
    t6 = presets !== undefined && <div className={cn("grid gap-3", presets.gridClassName)}>{presets.items.map((url, index) => <PresetThumb key={url} url={url} index={index} selected={selected === url} ariaLabel={c.presetAriaPrefix !== undefined ? `${c.presetAriaPrefix} ${index + 1}` : `${t("upload")} ${index + 1}`} circular={c.crop?.circular === true} buttonClassName={presets.itemClassName} onSelect={() => {
        c.setSelected(url);
        c.setError(null);
      }} />)}</div>;
    $[13] = c;
    $[14] = presets;
    $[15] = selected;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const t7 = c.handleDrop;
  let t8;
  let t9;
  if ($[18] !== c) {
    t8 = e_0 => {
      e_0.preventDefault();
      c.setDragOver(true);
    };
    t9 = () => {
      c.setDragOver(false);
    };
    $[18] = c;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  const t10 = c.dragOver ? "border-border-primary bg-foreground-primary/5" : "border-border-primary";
  const t11 = c.isUploading && "pointer-events-none opacity-50";
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = cn("flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-control border-2 border-dashed px-4 py-6 text-center transition-colors", t10, t11);
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      fileInputRef.current?.click();
    };
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== c.isUploading || $[26] !== t) {
    t14 = c.isUploading ? t("uploading") : t("upload");
    $[25] = c.isUploading;
    $[26] = t;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== c.isUploading || $[29] !== t14) {
    t15 = <AnimatedButton type="button" variant="secondary" size="sm" prefix={Upload01Icon} disabled={c.isUploading} loading={c.isUploading} onClick={t13}>{t14}</AnimatedButton>;
    $[28] = c.isUploading;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== c.dragOver || $[32] !== t) {
    t16 = c.dragOver ? t("drag_active") : t("upload_hint");
    $[31] = c.dragOver;
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <span className="text-caption text-foreground-secondary">{t16}</span>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("upload");
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== handleFileInput || $[39] !== t18) {
    t19 = <input ref={fileInputRef} type="file" accept={IMAGE_UPLOAD_ACCEPT_ATTR} onChange={handleFileInput} className="hidden" aria-label={t18} />;
    $[38] = handleFileInput;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== c.handleDrop || $[42] !== t12 || $[43] !== t15 || $[44] !== t17 || $[45] !== t19 || $[46] !== t8 || $[47] !== t9) {
    t20 = <div onDrop={t7} onDragOver={t8} onDragLeave={t9} className={t12}>{t15}{t17}{t19}</div>;
    $[41] = c.handleDrop;
    $[42] = t12;
    $[43] = t15;
    $[44] = t17;
    $[45] = t19;
    $[46] = t8;
    $[47] = t9;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== c.error) {
    t21 = c.error !== null && <p role="alert" className="text-body text-feedback-error-content">{c.error}</p>;
    $[49] = c.error;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t6) {
    t22 = <div className="flex flex-col gap-4">{t6}{t20}{t21}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t6;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== c.onOpenChange || $[56] !== c.open || $[57] !== c.title || $[58] !== selectionFooter || $[59] !== t22) {
    t23 = <SilkSheet open={t3} onOpenChange={t4} variant="detached" size="md" title={t5} footer={selectionFooter}>{t22}</SilkSheet>;
    $[55] = c.onOpenChange;
    $[56] = c.open;
    $[57] = c.title;
    $[58] = selectionFooter;
    $[59] = t22;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  return t23;
}
interface PresetThumbProps {
  url: string;
  index: number;
  selected: boolean;
  ariaLabel: string;
  circular: boolean;
  buttonClassName: string;
  onSelect: () => void;
}
function PresetThumb(t0) {
  const $ = _c(16);
  const {
    url,
    selected,
    ariaLabel,
    circular,
    buttonClassName,
    onSelect
  } = t0;
  const nsfw = useNsfwStatusByUrl(url);
  const t1 = selected ? "border-border-primary" : "border-transparent";
  let t2;
  if ($[0] !== buttonClassName || $[1] !== t1) {
    t2 = cn("relative overflow-hidden border-2", t1, buttonClassName);
    $[0] = buttonClassName;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = circular ? "avatar" : "thumbnail";
  let t4;
  if ($[3] !== nsfw.isFlagged || $[4] !== t3 || $[5] !== url) {
    t4 = <OptimizedImage src={url} alt="" variant={t3} size={64} className="size-full" imageClassName="object-cover" rounded="" nsfw={nsfw.isFlagged} preview={false} />;
    $[3] = nsfw.isFlagged;
    $[4] = t3;
    $[5] = url;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== selected) {
    t5 = selected && <div className="absolute inset-0 flex items-center justify-center bg-foreground-primary/20"><Icon icon={Tick02Icon} className="size-4 text-foreground-primary" aria-hidden="true" /></div>;
    $[7] = selected;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== ariaLabel || $[10] !== onSelect || $[11] !== selected || $[12] !== t2 || $[13] !== t4 || $[14] !== t5) {
    t6 = <button type="button" aria-pressed={selected} aria-label={ariaLabel} onClick={onSelect} className={t2}>{t4}{t5}</button>;
    $[9] = ariaLabel;
    $[10] = onSelect;
    $[11] = selected;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}